import AuthorDetailsModal from '@components/pages/become-author/AuthorDetailsModal'
import { colors, fontFamilies } from '@configs/styles/config'
import { useAuth } from '@contexts/Auth'
import styled from '@emotion/styled'
import {
  Button,
  Modal,
  ModalContent,
  ModalBody,
  useDisclosure,
} from '@nextui-org/react-latest'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

type Props = {
  removeButton?: boolean
}

const Edugram500 = ({ removeButton = false }: Props) => {
  const { t } = useTranslation('advantageous')
  const { isOpen, onOpen, onOpenChange } = useDisclosure()
  const [showBecomeAuthorModal, setShowBecomeAuthorModal] = useState(false)
  const { user, isAuthenticated, setShowAuthModal } = useAuth()
  const freshLoggedIn = useRef(false)
  const router = useRouter()

  useEffect(() => {
    if (user && !user?.isAuthor && freshLoggedIn.current) {
      setShowBecomeAuthorModal(true)
    }

    return () => {
      freshLoggedIn.current = false
    }
  }, [user])

  const handleBecomeCreator = () => {
    if (!isAuthenticated) {
      setShowAuthModal(true)
      freshLoggedIn.current = true
      return
    }
    if (user?.isAuthor) router.push('/author')
    else setShowBecomeAuthorModal(true)
  }

  const benefits = t('edugram500Modal.benefits', {
    returnObjects: true,
  }) as string[]

  return (
    <Container removeButton={removeButton}>
      <Wrapper>
        <Top>
          {t('noFeeWith')}{' '}
          <TopSpan onClick={onOpen} style={{ cursor: 'pointer' }}>
            {t('edugram500')}
          </TopSpan>
        </Top>
        <Middle>{t('becomeFirstAuthors')}</Middle>
        {!removeButton && (
          <>
            <Bottom>
              <BottomCaption>{t('learnMorePricing')}</BottomCaption>
              <Button
                color="secondary"
                variant="light"
                className="font-medium font-[500] py-[2rem] px-[4rem] rounded-full lg:text-lg text-base"
                style={{ border: '1px solid ' + colors.uguPurple }}
                as={Link}
                href="/pricing/"
              >
                {t('edugramPricing')}
              </Button>
            </Bottom>
          </>
        )}
      </Wrapper>

      <Modal
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        size="xl"
        classNames={{ backdrop: 'bg-uguPurple/40', closeButton: 'mt-3 mr-3' }}
        shouldBlockScroll={false}
      >
        <ModalContent className="rounded-[2.2rem] bg-[linear-gradient(82.09deg,#FFFFFF_3.8%,#FCF1FF_97.48%)]">
          <>
            <ModalBody className="modal-body-500 py-6">
              <ModalIcon>
                <svg
                  width="92"
                  height="96"
                  viewBox="0 0 92 96"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M46.1748 0C48.3872 0.131303 50.3202 0.98869 51.9736 2.57227C53.742 4.26602 55.5074 5.96309 57.2695 7.66309C58.1343 8.49922 58.9054 8.96458 60.0508 8.79785C62.2259 8.48161 64.4032 8.17655 66.582 7.88281C68.3129 7.64906 69.5544 7.5777 70.3057 7.66895C74.0438 8.12465 76.8851 11.0928 77.5234 14.7939C77.9577 17.3151 78.3919 19.8376 78.8262 22.3613C78.9996 23.3688 79.4558 24.0698 80.1934 24.4648C82.6131 25.7586 85.0317 27.053 87.4502 28.3467C91.7369 30.6398 92.9879 35.5204 90.9082 39.8066C89.8562 41.9753 88.8046 44.1452 87.7539 46.3164C87.1362 47.5931 87.0819 48.3898 87.7051 49.6064C88.1905 50.5552 89.0685 52.3591 90.3389 55.0166C91.125 56.6589 91.5941 57.814 91.7451 58.4814C92.5929 62.2313 90.6589 65.9867 87.2803 67.7773C84.9678 69.0036 82.6531 70.228 80.3369 71.4492C79.0139 72.1467 78.8974 73.2937 78.6055 74.9414C78.3347 76.4702 78.0645 77.9996 77.7949 79.5283C77.4818 81.3092 77.1958 82.5226 76.9375 83.1689C75.5504 86.6508 71.9211 88.8524 68.1455 88.3369C65.6921 88.0019 63.2385 87.6603 60.7852 87.3115C59.3458 87.1072 58.4733 87.1369 57.4795 88.1152C56.254 89.3227 54.7164 90.8078 52.8682 92.5703C50.6313 94.704 49.0629 95.8425 46.1484 96H45.7793C42.8967 95.8368 41.3191 94.7096 39.1084 92.5928C37.5472 91.0991 35.9967 89.5972 34.458 88.0859C33.5108 87.1541 32.6084 87.1106 31.2363 87.3037C29.2185 87.5874 27.2012 87.8701 25.1846 88.1514C22.7474 88.4907 20.7369 88.5634 18.5449 87.2734C16.3386 85.9747 14.9904 83.9917 14.5 81.3242C14.0495 78.8681 13.6064 76.4108 13.1709 73.9521C12.9725 72.8329 12.5979 71.9759 11.6416 71.4678C9.35302 70.2491 7.06397 69.0316 4.77539 67.8154C0.664762 65.6311 -1.22257 60.889 0.84375 56.6104C2.0505 54.1141 3.26135 51.6201 4.47559 49.1289C4.95667 48.1408 4.63604 47.1957 4.125 46.1514C2.5004 42.8368 1.41016 40.5818 0.853516 39.3867C-0.685183 36.0774 -0.0190356 32.1432 2.64648 29.6738C3.1918 29.1689 4.24071 28.4976 5.79297 27.6602C7.59626 26.6864 9.40336 25.7177 11.2129 24.7539C12.3266 24.1596 12.9123 23.5574 13.1182 22.3594C13.8319 18.1911 14.3199 15.4607 14.582 14.168C15.4618 9.8256 19.6996 7.09163 24.0273 7.69141C26.6281 8.05141 29.2301 8.41402 31.832 8.78027C33.0672 8.95457 33.7748 8.54074 34.7012 7.65039C36.4682 5.94923 38.2324 4.24522 39.9932 2.53906C41.608 0.974119 43.5443 0.127547 45.8018 0H46.1748ZM48.2109 6.62402C47.3388 5.78992 46.5602 5.39462 45.4111 5.62695C44.4864 5.81445 44.0364 6.35766 43.3438 7.01953C40.7904 9.45453 39.1279 11.0546 38.3555 11.8184C36.9279 13.232 35.2631 14.0546 33.3613 14.2871C32.64 14.3759 31.5693 14.3236 30.1504 14.1299C28.1039 13.8512 26.0572 13.5631 24.0107 13.2656C23.1048 13.1344 22.4275 13.1248 21.9795 13.2373C20.8341 13.5261 20.1438 14.3333 19.9092 15.6582C19.551 17.6982 19.1924 19.7384 18.833 21.7783C18.5248 23.532 18.2218 24.7406 17.9248 25.4043C17.1236 27.193 15.7605 28.6042 13.835 29.6367C11.8385 30.7066 9.83939 31.7733 7.83789 32.8369C5.9435 33.8438 4.87262 35.0718 5.96582 37.3105C7.09141 39.6092 8.20828 41.9109 9.31641 44.2158C10.5418 46.7644 10.5363 49.3123 9.2998 51.8584C8.16167 54.1984 7.01964 56.5389 5.87402 58.8789C4.93249 60.8026 5.93793 62.1338 7.59082 63.0244C9.83189 64.2305 12.0773 65.4297 14.3271 66.6221C16.9441 68.0096 18.114 70.3578 18.6494 73.249C19.0899 75.6264 19.5106 78.0063 19.9111 80.3887C20.1308 81.6861 20.8135 82.4776 21.959 82.7627C22.417 82.8764 23.1085 82.8654 24.0332 82.7305C25.8313 82.4705 27.6303 82.2151 29.4297 81.9639C31.1743 81.7201 32.4158 81.6359 33.1533 81.7109C35.1163 81.9122 36.8647 82.7525 38.3984 84.2324C40.1643 85.9349 41.9319 87.6359 43.7002 89.3359C45.2107 90.7871 46.7136 90.8289 48.2354 89.3555C49.9113 87.7305 51.5945 86.1115 53.2842 84.499C55.9629 81.9453 58.0389 81.3636 61.749 81.8623C63.8193 82.141 65.889 82.4319 67.958 82.7344C70.2174 83.0663 71.6198 82.5616 72.0615 80.1484C72.522 77.6373 72.9669 75.1242 73.3975 72.6094C73.7831 70.3619 74.8545 68.5874 76.6104 67.2861C76.9662 67.0223 78.0491 66.4046 79.8594 65.4336C81.1772 64.7273 82.4971 64.0256 83.8174 63.3281C85.8745 62.2407 87.1617 61.1152 85.96 58.6572C85.049 56.7949 84.1439 54.9299 83.2441 53.0625C82.4804 51.4775 82.0259 50.3184 81.8799 49.5859C81.5218 47.7898 81.766 46.0105 82.6133 44.248C83.7339 41.9206 84.8617 39.5959 85.9961 37.2734C86.977 35.2672 86.1473 33.9319 84.4102 33C82.7118 32.0888 81.0124 31.1789 79.3115 30.2715C77.7179 29.4227 76.6582 28.7686 76.1328 28.3086C73.8959 26.353 73.5831 24.3411 73.001 21.0918C72.7264 19.5581 72.4562 18.024 72.1904 16.4902C72.0133 15.4631 71.7975 14.7489 71.543 14.3477C70.9215 13.3714 69.9331 12.9808 68.5791 13.1758C66.5613 13.4645 64.5425 13.7505 62.5234 14.0342C60.7529 14.2829 59.5114 14.3748 58.7988 14.3086C56.8708 14.1286 55.1708 13.326 53.6982 11.9023C51.8737 10.1386 50.0442 8.37902 48.2109 6.62402ZM40.252 54.5361H35.6494L35.3643 57.0947C35.5174 57.0051 35.7494 56.9098 36.0605 56.8096C36.3721 56.704 36.721 56.6514 37.1064 56.6514C37.6662 56.6514 38.1627 56.7389 38.5957 56.9131C39.0288 57.0874 39.3962 57.3411 39.6973 57.6738C40.0035 58.0065 40.2361 58.413 40.3945 58.8936C40.553 59.3741 40.6318 59.9181 40.6318 60.5254C40.6318 61.0377 40.553 61.5264 40.3945 61.9912C40.2361 62.4507 39.996 62.8631 39.6738 63.2275C39.3517 63.5866 38.9477 63.8692 38.4619 64.0752C37.976 64.2812 37.3999 64.3838 36.7344 64.3838C36.2379 64.3838 35.7568 64.31 35.292 64.1621C34.8328 64.0143 34.4184 63.7952 34.0488 63.5049C33.6844 63.2144 33.3908 62.8631 33.1689 62.4512C32.9524 62.0339 32.8387 61.5579 32.8281 61.0244H35.0947C35.1264 61.3517 35.2105 61.6345 35.3477 61.8721C35.4902 62.1044 35.6779 62.2844 35.9102 62.4111C36.1426 62.5379 36.4149 62.6016 36.7266 62.6016C37.0169 62.6015 37.2648 62.5454 37.4707 62.4346C37.6767 62.3237 37.8439 62.171 37.9707 61.9756C38.0974 61.7749 38.19 61.5424 38.248 61.2783C38.3114 61.0091 38.3427 60.7187 38.3428 60.4072C38.3428 60.0956 38.3054 59.8121 38.2314 59.5586C38.1575 59.3053 38.0437 59.0861 37.8906 58.9014C37.7375 58.7166 37.5422 58.5739 37.3047 58.4736C37.0724 58.3734 36.8006 58.3233 36.4893 58.3232C36.0669 58.3232 35.7392 58.3895 35.5068 58.5215C35.2797 58.6535 35.0887 58.8036 34.9355 58.9727L33.1143 58.5371L33.7715 52.6904H40.252V54.5361ZM45.9639 52.5322C46.4287 52.5322 46.8597 52.5924 47.2559 52.7139C47.6572 52.8301 48.0187 53.0126 48.3408 53.2607C48.663 53.509 48.9379 53.8289 49.165 54.2197C49.392 54.6051 49.5661 55.0671 49.6875 55.6055C49.809 56.1388 49.8701 56.7516 49.8701 57.4434V59.4404C49.8701 60.3065 49.7776 61.0541 49.5928 61.6826C49.4079 62.3057 49.1405 62.8184 48.792 63.2197C48.4488 63.6156 48.0396 63.9086 47.5645 64.0986C47.0891 64.2888 46.5605 64.3838 45.9795 64.3838C45.5149 64.3838 45.0819 64.3261 44.6807 64.21C44.2794 64.0885 43.9178 63.9008 43.5957 63.6475C43.2788 63.394 43.0039 63.074 42.7715 62.6885C42.5444 62.2976 42.3695 61.8327 42.248 61.2939C42.1266 60.7553 42.0664 60.1374 42.0664 59.4404V57.4434C42.0664 56.5774 42.159 55.8356 42.3438 55.2178C42.5339 54.5946 42.8003 54.0846 43.1436 53.6885C43.4921 53.2924 43.9045 53.0022 44.3799 52.8174C44.8551 52.6273 45.383 52.5323 45.9639 52.5322ZM55.2812 52.5322C55.7459 52.5322 56.1762 52.5924 56.5723 52.7139C56.9737 52.8301 57.336 53.0125 57.6582 53.2607C57.9803 53.5089 58.2544 53.829 58.4814 54.2197C58.7085 54.6052 58.8834 55.0669 59.0049 55.6055C59.1263 56.1388 59.1865 56.7516 59.1865 57.4434V59.4404C59.1865 60.3065 59.094 61.0541 58.9092 61.6826C58.7243 62.3057 58.4579 62.8184 58.1094 63.2197C57.7662 63.6156 57.3569 63.9085 56.8818 64.0986C56.4065 64.2888 55.8779 64.3838 55.2969 64.3838C54.8323 64.3838 54.3993 64.3261 53.998 64.21C53.5967 64.0885 53.2343 63.9009 52.9121 63.6475C52.5952 63.3939 52.3203 63.074 52.0879 62.6885C51.8608 62.2977 51.6869 61.8326 51.5654 61.2939C51.444 60.7553 51.3828 60.1375 51.3828 59.4404V57.4434C51.3828 56.5775 51.4754 55.8356 51.6602 55.2178C51.8503 54.5945 52.1176 54.0846 52.4609 53.6885C52.8094 53.2925 53.2211 53.0022 53.6963 52.8174C54.1716 52.6272 54.7003 52.5322 55.2812 52.5322ZM45.9639 54.3145C45.7052 54.3145 45.4751 54.3645 45.2744 54.4648C45.0791 54.5652 44.9126 54.7265 44.7754 54.9482C44.6382 55.1647 44.5329 55.4552 44.459 55.8193C44.3903 56.1785 44.3555 56.6172 44.3555 57.1348V59.7412C44.3555 60.1585 44.3767 60.5259 44.4189 60.8428C44.4665 61.1594 44.5353 61.4312 44.625 61.6582C44.7201 61.88 44.8313 62.0625 44.958 62.2051C45.0899 62.3423 45.2403 62.4425 45.4092 62.5059C45.5833 62.5692 45.7737 62.6015 45.9795 62.6016C46.2329 62.6016 46.4579 62.5514 46.6533 62.4512C46.8539 62.3456 47.0229 62.1816 47.1602 61.96C47.3028 61.7328 47.4089 61.4367 47.4775 61.0723C47.5462 60.7078 47.5801 60.2641 47.5801 59.7412V57.1348C47.5801 56.7175 47.5563 56.3526 47.5088 56.041C47.4665 55.7296 47.4003 55.4655 47.3105 55.249C47.2209 55.0275 47.1103 54.8482 46.9785 54.7109C46.8465 54.5736 46.6954 54.4726 46.5264 54.4092C46.3574 54.3459 46.1697 54.3145 45.9639 54.3145ZM55.2812 54.3145C55.0225 54.3145 54.7925 54.3645 54.5918 54.4648C54.3964 54.5652 54.2301 54.7264 54.0928 54.9482C53.9555 55.1647 53.8493 55.455 53.7754 55.8193C53.7067 56.1785 53.6729 56.6172 53.6729 57.1348V59.7412C53.6729 60.1585 53.6941 60.5259 53.7363 60.8428C53.7838 61.1595 53.8527 61.4312 53.9424 61.6582C54.0374 61.8799 54.1478 62.0625 54.2744 62.2051C54.4064 62.3424 54.5576 62.4425 54.7266 62.5059C54.9009 62.5692 55.0909 62.6016 55.2969 62.6016C55.5504 62.6016 55.7753 62.5515 55.9707 62.4512C56.1713 62.3456 56.3403 62.1816 56.4775 61.96C56.6201 61.7329 56.7253 61.4367 56.7939 61.0723C56.8626 60.7078 56.8975 60.2641 56.8975 59.7412V57.1348C56.8975 56.7175 56.8737 56.3526 56.8262 56.041C56.7839 55.7296 56.7177 55.4655 56.6279 55.249C56.5382 55.0273 56.4269 54.8482 56.2949 54.7109C56.1629 54.5736 56.0127 54.4726 55.8438 54.4092C55.6748 54.3458 55.4871 54.3145 55.2812 54.3145ZM45.0605 36.668C45.5227 36.668 45.9175 36.7517 46.2432 36.9189C46.5348 37.0667 46.7826 37.2721 46.9883 37.5332L47.0547 36.7998H48.501V43.7461C48.5009 44.3884 48.3645 44.934 48.0918 45.3828C47.8189 45.8317 47.4377 46.173 46.9492 46.4062C46.4607 46.6439 45.8946 46.7627 45.252 46.7627C44.9793 46.7626 44.676 46.7227 44.3418 46.6436C44.0117 46.5643 43.6895 46.4368 43.377 46.2607C43.0691 46.0891 42.8112 45.8626 42.6045 45.5811L43.3506 44.6436C43.6059 44.9473 43.8882 45.1697 44.1963 45.3105C44.5043 45.4513 44.8282 45.5215 45.167 45.5215C45.5321 45.5214 45.8425 45.4538 46.0977 45.3174C46.3573 45.1854 46.5574 44.9891 46.6982 44.7295C46.8391 44.4698 46.9092 44.1524 46.9092 43.7783V43.2686C46.7092 43.5013 46.4745 43.6877 46.2031 43.8252C45.8775 43.9924 45.4923 44.0762 45.0479 44.0762C44.6121 44.0762 44.2183 43.9851 43.8662 43.8047C43.5185 43.6242 43.2187 43.3716 42.9678 43.0459C42.7214 42.7203 42.5324 42.3374 42.4004 41.8975C42.2683 41.4529 42.2021 40.9705 42.2021 40.4512V40.3125C42.2022 39.7713 42.2684 39.2784 42.4004 38.834C42.5324 38.3852 42.7214 37.9999 42.9678 37.6787C43.2143 37.353 43.5141 37.1043 43.8662 36.9326C44.2182 36.7566 44.6162 36.668 45.0605 36.668ZM33.2891 43.9434H31.8438L31.7646 43.1777C31.5457 43.4536 31.2844 43.6702 30.9785 43.8252C30.653 43.9923 30.2724 44.0761 29.8369 44.0762C29.4056 44.0762 29.0157 43.9851 28.668 43.8047C28.3248 43.6243 28.0321 43.3715 27.79 43.0459C27.548 42.7203 27.363 42.3374 27.2354 41.8975C27.1077 41.4529 27.0439 40.9705 27.0439 40.4512V40.3125C27.044 39.7713 27.1078 39.2784 27.2354 38.834C27.363 38.3851 27.548 38 27.79 37.6787C28.0321 37.353 28.3271 37.1043 28.6748 36.9326C29.0225 36.7566 29.4139 36.668 29.8496 36.668C30.2809 36.668 30.6596 36.7517 30.9854 36.9189C31.2537 37.0567 31.4888 37.2441 31.6914 37.4805V33.8027H33.2891V43.9434ZM36.5107 41.4287C36.5108 41.6882 36.5419 41.9061 36.6035 42.082C36.6651 42.2537 36.7489 42.3924 36.8545 42.498C36.9601 42.6036 37.0838 42.6786 37.2246 42.7227C37.3652 42.7666 37.5147 42.7881 37.6729 42.7881C38.1262 42.7881 38.4835 42.7005 38.7432 42.5244C38.9692 42.3699 39.1378 42.1696 39.251 41.9248V36.7998H40.8486V43.9434H39.3438L39.3027 43.2139C39.0922 43.4724 38.8315 43.6786 38.5186 43.8311C38.1752 43.9939 37.763 44.0762 37.2832 44.0762C36.9357 44.0761 36.6165 44.025 36.3262 43.9238C36.0358 43.8226 35.7845 43.6663 35.5732 43.4551C35.3665 43.2439 35.2062 42.9686 35.0918 42.6299C34.9774 42.291 34.9199 41.886 34.9199 41.415V36.7998H36.5107V41.4287ZM57.5459 36.668C58.0827 36.668 58.5581 36.7581 58.9717 36.9385C59.3898 37.1189 59.7174 37.3925 59.9551 37.7578C60.1971 38.1186 60.3183 38.5716 60.3184 39.1172V42.293C60.3184 42.6185 60.3408 42.9113 60.3848 43.1709C60.4332 43.4261 60.5009 43.6487 60.5889 43.8379V43.9434H58.958C58.8833 43.7718 58.8242 43.5539 58.7803 43.29C58.7775 43.2715 58.776 43.2528 58.7734 43.2344C58.7097 43.3041 58.6431 43.3749 58.5693 43.4424C58.3669 43.6272 58.122 43.7786 57.8359 43.8975C57.5544 44.0162 57.2289 44.0761 56.8594 44.0762C56.3929 44.0762 55.9766 43.9836 55.6113 43.7988C55.246 43.6096 54.9598 43.356 54.7529 43.0391C54.5462 42.7179 54.4424 42.355 54.4424 41.9502C54.4424 41.5717 54.5135 41.2368 54.6543 40.9463C54.7995 40.6515 55.0109 40.405 55.2881 40.207C55.5697 40.0091 55.9126 39.859 56.3174 39.7578C56.7223 39.6522 57.1847 39.5996 57.7041 39.5996H58.7207V39.1045C58.7207 38.8493 58.6744 38.6292 58.582 38.4443C58.4896 38.2595 58.3484 38.1159 58.1592 38.0146C57.9744 37.9136 57.7413 37.8633 57.46 37.8633C57.2003 37.8633 56.9754 37.9071 56.7861 37.9951C56.5969 38.0831 56.4494 38.202 56.3438 38.3516C56.2381 38.5012 56.1855 38.6711 56.1855 38.8604H54.6006C54.6006 38.5787 54.6692 38.3051 54.8057 38.041C54.9421 37.7772 55.14 37.5417 55.3994 37.335C55.6591 37.1281 55.9701 36.9655 56.3311 36.8467C56.6919 36.7279 57.097 36.668 57.5459 36.668ZM26.2383 35.6514H21.6367V38.3447H25.6045V39.6387H21.6367V42.6299H26.2715V43.9434H19.9795V34.3311H26.2383V35.6514ZM53.3662 36.668C53.463 36.668 53.5667 36.6767 53.6768 36.6943C53.7912 36.7119 53.8774 36.7319 53.9346 36.7539L53.9209 38.2324C53.8242 38.2149 53.7187 38.2022 53.6045 38.1934C53.4946 38.1846 53.3843 38.1797 53.2744 38.1797C53.0017 38.1797 52.7615 38.2197 52.5547 38.2988C52.3479 38.3736 52.174 38.4838 52.0332 38.6289C51.9151 38.7508 51.821 38.8964 51.749 39.0645V43.9434H50.1582V36.7998H51.6768L51.7207 37.6426C51.7303 37.626 51.7392 37.6091 51.749 37.5928C51.9294 37.3023 52.1535 37.075 52.4219 36.9121C52.6947 36.7493 53.0098 36.668 53.3662 36.668ZM69.7861 36.668C70.1513 36.668 70.4767 36.7191 70.7627 36.8203C71.0531 36.9171 71.2995 37.0711 71.502 37.2822C71.7044 37.4935 71.8592 37.7685 71.9648 38.1074C72.0705 38.4419 72.123 38.8469 72.123 39.3223V43.9434H70.5254V39.3154C70.5254 38.9549 70.4727 38.6755 70.3672 38.4775C70.266 38.2795 70.1199 38.1422 69.9307 38.0674C69.7415 37.9883 69.5148 37.9492 69.251 37.9492C69.0046 37.9492 68.7869 37.9955 68.5977 38.0879C68.4129 38.1759 68.2565 38.3011 68.1289 38.4639C68.0023 38.6211 67.9049 38.802 67.8389 39.0068C67.8408 39.0646 67.8447 39.1235 67.8447 39.1836V43.9434H66.2539V39.3223C66.2539 38.9748 66.2012 38.7019 66.0957 38.5039C65.9945 38.306 65.8468 38.1647 65.6533 38.0811C65.4641 37.993 65.2367 37.9492 64.9727 37.9492C64.6735 37.9493 64.4182 38.0067 64.207 38.1211C64.0003 38.2355 63.8302 38.3918 63.6982 38.5898C63.6516 38.6599 63.6111 38.7347 63.5732 38.8125V43.9434H61.9824V36.7998H63.4805L63.5312 37.6064C63.7635 37.313 64.0485 37.086 64.3857 36.9258C64.7422 36.7542 65.1537 36.668 65.6201 36.668C65.9456 36.668 66.2424 36.7168 66.5107 36.8135C66.7836 36.9059 67.0197 37.0534 67.2178 37.2559C67.3653 37.4034 67.4859 37.5818 67.582 37.79C67.6237 37.7291 67.6669 37.6695 67.7129 37.6123C67.9506 37.3174 68.2431 37.0862 68.5908 36.9189C68.9385 36.7517 69.3372 36.668 69.7861 36.668ZM57.8232 40.583C57.5284 40.583 57.2684 40.6118 57.0439 40.6689C56.8195 40.7218 56.6321 40.8016 56.4824 40.9072C56.3329 41.0128 56.2203 41.1405 56.1455 41.29C56.0709 41.4395 56.0332 41.6089 56.0332 41.7979C56.0332 41.9871 56.078 42.1609 56.166 42.3193C56.254 42.4734 56.3816 42.5946 56.5488 42.6826C56.7204 42.7706 56.927 42.8144 57.1689 42.8145C57.4944 42.8145 57.7785 42.749 58.0205 42.6172C58.267 42.4807 58.4607 42.3148 58.6016 42.1211C58.6495 42.0537 58.6881 41.9867 58.7207 41.9219V40.583H57.8232ZM30.252 37.9492C29.9617 37.9493 29.7151 38.0106 29.5127 38.1338C29.3103 38.257 29.1427 38.427 29.0107 38.6426C28.8833 38.858 28.7891 39.1088 28.7275 39.3945C28.6659 39.6806 28.6348 39.9868 28.6348 40.3125V40.4512C28.6348 40.7769 28.6635 41.0806 28.7207 41.3623C28.7823 41.644 28.8772 41.8927 29.0049 42.1084C29.1325 42.3196 29.2976 42.4871 29.5 42.6104C29.7068 42.7291 29.9533 42.7881 30.2393 42.7881C30.6002 42.7881 30.8976 42.7092 31.1309 42.5508C31.364 42.3923 31.5467 42.1786 31.6787 41.9102C31.6835 41.9007 31.6868 41.8904 31.6914 41.8809V38.8457C31.6277 38.7159 31.5537 38.5968 31.4668 38.4902C31.3305 38.3188 31.1612 38.1871 30.959 38.0947C30.7609 37.9979 30.5248 37.9492 30.252 37.9492ZM45.457 37.9492C45.171 37.9492 44.9222 38.0106 44.7109 38.1338C44.4997 38.257 44.3259 38.4269 44.1895 38.6426C44.0575 38.8581 43.9586 39.1087 43.8926 39.3945C43.8266 39.6806 43.793 39.9868 43.793 40.3125V40.4512C43.793 40.7769 43.8241 41.0806 43.8857 41.3623C43.9518 41.644 44.0506 41.8927 44.1826 42.1084C44.3191 42.3197 44.4914 42.4871 44.6982 42.6104C44.9094 42.729 45.1584 42.7881 45.4443 42.7881C45.8181 42.788 46.1238 42.7091 46.3613 42.5508C46.5997 42.3947 46.7818 42.1847 46.9092 41.9219V38.8057C46.8499 38.6915 46.783 38.5857 46.7051 38.4902C46.5686 38.3186 46.3963 38.1872 46.1895 38.0947C45.9826 37.998 45.7386 37.9492 45.457 37.9492Z"
                    fill="#7181FF"
                  />
                </svg>
              </ModalIcon>

              <ModalTitleSection>
                <ModalTitle>{t('edugram500Modal.title')}</ModalTitle>
                <ModalDescription>
                  {t('edugram500Modal.description')}
                </ModalDescription>
              </ModalTitleSection>
              <ModalBenefits>
                {benefits.map((benefit, index) => (
                  <BenefitItem key={index}>
                    <CheckIcon>
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M16.6666 5L7.49998 14.1667L3.33331 10"
                          stroke="#2B1C50"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </CheckIcon>
                    <BenefitText>{benefit}</BenefitText>
                  </BenefitItem>
                ))}
              </ModalBenefits>
              <Button
                onPress={handleBecomeCreator}
                color="secondary"
                radius="full"
                size="lg"
              >
                {t('edugram500Modal.joinNow')}
              </Button>
            </ModalBody>
          </>
        </ModalContent>
      </Modal>
      {showBecomeAuthorModal && (
        <AuthorDetailsModal
          isOpen={showBecomeAuthorModal}
          onClose={(ask?: boolean) => {
            if (ask) {
              if (confirm('Are you sure you want to close?')) {
                setShowBecomeAuthorModal((prev) => !prev)
              }
            } else {
              setShowBecomeAuthorModal((prev) => !prev)
            }
          }}
        />
      )}
    </Container>
  )
}

export default Edugram500

const Container = styled.div`
  min-height: 568px;
  margin: 6rem auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-bottom: ${(props) => (props.removeButton ? '1.5rem' : '6rem')};
`
const Wrapper = styled.div`
  max-width: 1600px;
  display: flex;
  flex-direction: column;

  align-items: center;
  margin: 0 auto;
  gap: 2.5rem;
  @media (min-width: 640px) {
    gap: 4rem;
  }
`
const Top = styled.div`
  font-family: ${fontFamilies.bold};
  text-align: center;
  color: ${colors.uguPurple};
  gap: 0.75rem;
  font-size: 64px;
  line-height: 64px;
  @media screen and (max-width: 1024px) {
    line-height: 3.8rem;
    font-size: 3.8rem;
  }
  @media screen and (min-width: 1192px) {
    display: flex;
  }
  @media screen and (max-width: 596px) {
    font-size: 42px;
    line-height: 42px;
  }
`

const TopSpan = styled.div`
  font-family: ${fontFamilies.bold};
  color: ${colors.uguBrightPurple};
  border-bottom: 3px solid ${colors.uguBrightPurple};
  font-size: 64px;
  line-height: 64px;
  @media screen and (max-width: 1024px) {
    line-height: 50px;
    font-size: 50px;
  }
  @media screen and (max-width: 596px) {
    font-size: 42px;
    line-height: 42px;
  }
`
const Middle = styled.div`
  font-family: ${fontFamilies.medium};
  text-align: center;
  color: ${colors.uguPurple};
  max-width: 720px;
  font-size: 20px;
  line-height: 33.68px;
  @media screen and (max-width: 1024px) {
    font-size: 21px;
    line-height: 32.68px;
    padding: 0px 0.6rem;
  }
  @media screen and (max-width: 596px) {
    font-size: 16px;
    line-height: 18px;
  }
`
const Bottom = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`
const BottomCaption = styled.div`
  font-family: ${fontFamilies.OpenSansRegular};
  font-size: 14px;
  font-weight: 500;
  line-height: 19.07px;
  text-align: center;
  color: ${colors.uguPurple};
`

const ModalIcon = styled.div`
  margin-bottom: 1rem;
  @media (min-width: 640px) {
    margin-bottom: 2rem;
  }
`

const ModalTitleSection = styled.div`
  margin-bottom: 1rem;
`

const ModalTitle = styled.div`
  font-family: ${fontFamilies.bold};
  font-size: 28px;
  color: ${colors.uguPurple};
  margin-bottom: 1rem;
  line-height: 1.2;
  @media (min-width: 640px) {
    font-size: 32px;
  }
`

const ModalDescription = styled.p`
  font-family: ${fontFamilies.medium};
  font-size: 21px;
  color: ${colors.uguPurple};
  line-height: 1.4;
  opacity: 0.8;
  @media (min-width: 640px) {
    font-size: 24px;
  }
`

const ModalBenefits = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 2rem;
`

const BenefitItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
`

const CheckIcon = styled.div`
  flex-shrink: 0;
  margin-top: 0.25rem;
`

const BenefitText = styled.p`
  font-family: ${fontFamilies.regular};
  color: ${colors.uguPurple};
  line-height: 1.5;
  margin: 0;
  font-size: 16px;
  @media (min-width: 640px) {
    font-size: 18px;
  }
`
